import { RewardUnlockStrategy } from '@brandclub/types';
import { Box, Skeleton, Typography, styled, useMediaQuery } from '@mui/material';
import React from 'react';

import { ShoppingCartItem } from './type';
import {
  formatMoney,
  getAppConsistentParams,
  getEntityImage,
  getProductUrl,
  getPurchaseRewardAmountForProduct,
  resolveProductImageUrls,
} from '../../utils';
import { FallbackImage } from '../../Components';
import { RewardPillWithText } from '../components';
import { CampaignType, ProductRewardSearchResult } from '../../types';
import { QuantityControl } from './QuantityControl';
import { AmazonDeliveryMessage } from './AmazonDelivery';
import { SubscriberPrice, SubscriptionEyebrow, SubscriptionLineDetails } from './SubscriptionLine';
import { isSubscriptionLine } from './lineIdentity';
import type { SubscribeAndSaveOffer, SubscriptionFrequency } from './subscribeAndSave';

const CartProductContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  textDecoration: 'none',
  padding: '16px 0',
  color: theme.vars.palette.text.primary,

  alignItems: 'flex-start',
  gap: 8,

  '.product_image': {
    objectFit: 'contain',
    width: 84,
    height: 84,
    backgroundColor: '#f6f8fa',
  },

  '.product_detail': {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',

    width: '100%',
    gap: 8,
    '.info': {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    '.name': {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: '20px',
    },
    '.bc-actions': {
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    '.price': {
      display: 'flex',
      alignItems: 'center',
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '20px',
      gap: 8,
    },
    '.retailer_logo': {
      width: 24,
      height: 24,
      img: {
        width: 24,
        height: 24,
        objectFit: 'contain',
      },
    },
  },
}));

export const CartProduct = ({
  product,
  handleAddQuantity,
  handleMinusQuantity,
  cartProducts,
  storeBrandingType,
  prime,
  subscriptionOffer,
  handleChangeSubscription,
}: {
  product: ShoppingCartItem;
  /** The offer of a subscription line: its frequencies and subscriber price. */
  subscriptionOffer?: SubscribeAndSaveOffer;
  /** Sets the frequency of a subscription line. A host without it shows none. */
  handleChangeSubscription?: (product: ShoppingCartItem, frequency: SubscriptionFrequency) => void;
  /** Shows Amazon's Prime badge on this line. */
  prime?: { targetId: string; amazonSku: string };
  handleAddQuantity: (product: ShoppingCartItem) => void;
  handleMinusQuantity: (product: ShoppingCartItem) => void;
  cartProducts?: { [retailerSku: string]: ProductRewardSearchResult };
  storeBrandingType: string;
}) => {
  const cartProduct = cartProducts?.[product.stacklineSku];
  const isSubscription = !!handleChangeSubscription && isSubscriptionLine(product);
  const metadata = cartProduct ?? (product as ShoppingCartItem & ProductRewardSearchResult);
  // hasActiveInstantReward on metadata is not always set, so we need to check the campaignRewards as well
  const hasActiveInstantReward =
    !!metadata?.hasActiveInstantReward ||
    (!!metadata?.campaignRewards &&
      metadata?.campaignRewards.some(
        (campaignReward) =>
          campaignReward?.campaignType === CampaignType.DailyDealsRewardCampaign ||
          (campaignReward &&
            'rewardUnlockStrategy' in campaignReward &&
            campaignReward.rewardUnlockStrategy === RewardUnlockStrategy.Instant),
      ));

  const rewardAmount = metadata && getPurchaseRewardAmountForProduct(metadata);
  const rewardPercent = !!metadata?.retailPrice && !!rewardAmount ? (rewardAmount * 100) / metadata.retailPrice : 0;

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const urlSearchParams = getAppConsistentParams(window.location.search);

  if (!metadata?.title) {
    return (
      <CartProductContainer className={'cart_product'}>
        <Skeleton className="product_image" />
        <Box className="product_detail">
          <Box className="info">
            <Skeleton className="price" />
            <Skeleton className="name" />
          </Box>
        </Box>
      </CartProductContainer>
    );
  }

  return (
    <CartProductContainer className={'cart_product'}>
      <a
        href={getProductUrl({
          title: metadata.title,
          stacklineSku: metadata.stacklineSku,
          urlSearchParams,
          dtcProductPageUrl: metadata.dtcProductPageUrl,
          storeType: storeBrandingType,
        })}
        style={{ textDecoration: 'none' }}
        target="_blank"
      >
        <Box>
          <FallbackImage
            title={metadata?.title ?? product.stacklineSku}
            className="product_image"
            {...resolveProductImageUrls(metadata)}
          />
        </Box>
      </a>
      <Box className={'product_detail'}>
        <Box className={'info'}>
          {isSubscription && <SubscriptionEyebrow offer={subscriptionOffer} />}
          <Typography
            className={'name'}
            title={metadata?.title}
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
            }}
          >
            {metadata?.title ?? 'Unknown Product'}
          </Typography>
          {prime ? (
            <AmazonDeliveryMessage
              targetId={prime.targetId}
              context="cart"
              messageType="none"
              items={[{ amazonSku: prime.amazonSku, quantity: product.quantity }]}
            />
          ) : null}
          {isSubscription && product.subscriptionFrequency ? (
            <SubscriptionLineDetails
              frequency={product.subscriptionFrequency}
              offer={subscriptionOffer}
              onChange={(frequency) => handleChangeSubscription?.(product, frequency)}
            />
          ) : null}
        </Box>
        <Box className={'bc-actions'}>
          <Box className={'price'}>
            <Box className={'retailer_logo'}>
              <img
                src={metadata?.retailerShortLogo ?? getEntityImage(product.retailerId as number, 'retailer')}
                alt={`Logo for retailer: ${product.retailerId}`}
              />
            </Box>
            {isSubscription && subscriptionOffer ? (
              <SubscriberPrice offer={subscriptionOffer} />
            ) : (
              formatMoney(metadata?.retailPrice ?? product.retailPrice)
            )}
            {!!rewardAmount && (
              <RewardPillWithText
                rewardAmount={rewardAmount}
                rewardPercentText="reward"
                baseFontSize={isMobileView ? 12 : 14}
                pillContainerStyle={{
                  boxSizing: 'border-box',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                rewardPercent={rewardPercent}
                rewardCampaignType={
                  hasActiveInstantReward ? CampaignType.DailyDealsRewardCampaign : CampaignType.PurchaseRewardCampaign
                }
              />
            )}
          </Box>
          <QuantityControl
            count={product.quantity}
            onAddClick={() => handleAddQuantity(product)}
            onMinusClick={() => handleMinusQuantity(product)}
            trackedParams={product}
          />
        </Box>
      </Box>
    </CartProductContainer>
  );
};
